import { inject, injectable } from 'inversify';
import { makeAutoObservable, when } from 'mobx';
import { initialValues } from '@mobx/services/osi/config';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { getOsiDocsById, getOsiInfoById } from '@shared/api/osi';
import { HistoryModule } from '@mobx/services/history';
import { PATH_CABINET } from '@app/routes/paths';
import api from '@app/api';
import { flow } from 'lodash';
import { clearPersistedStore, isHydrated, makePersistable } from 'mobx-persist-store';
import { type OsiService } from '@shared/types/mobx/services/osi';
import { type RolesService, RolesServiceIdentifier } from '@/shared/types/mobx/services/roles';

@injectable()
export class OsiServiceImpl implements OsiService {
  osiId = initialValues.osiId;

  osiInfo = initialValues.osiInfo;

  osiDocs = initialValues.osiDocs;

  osiMenus = initialValues.osiMenus;

  isLoading = initialValues.isLoading;

  isLockedMenus = initialValues.isLockedMenus;

  constructor(private history: HistoryModule, @inject(RolesServiceIdentifier) private rolesModule: RolesService) {
    makeAutoObservable(this);
    makePersistable(this, { name: 'OsiService', properties: ['osiId'] });

    when(
      () => isHydrated(this),
      async () => {
        await this.hydrateOsi();
      }
    );
  }

  /**
   * Завершилась ли гидратация persist-стора (OSI-806).
   *
   * ЗАЧЕМ НАРУЖУ: раздел /osi нужно уводить в /cabinet, если дом не выбран (osiId пуст).
   * Но `osiId` персистится через `makePersistable` и восстанавливается АСИНХРОННО, поэтому
   * на первом рендере он пуст даже у пользователя с выбранным домом. Решение «дома нет»
   * можно принимать ТОЛЬКО после того, как гидратация завершилась.
   *
   * РЕАКТИВНОСТЬ: `isHydrated()` читает поле `isHydrated` объекта PersistStore, объявленное
   * через `makeObservable(..., { isHydrated: observable })` в самом mobx-persist-store.
   * Поэтому геттер (computed от makeAutoObservable) корректно перевычисляется, а observer-
   * компоненты — перерисовываются. Косвенное доказательство рядом: `when(() => isHydrated(this))`
   * в конструкторе работает только на реактивном выражении.
   */
  get isStoreHydrated(): boolean {
    return isHydrated(this);
  }

  get menuItems() {
    const getAdaptedActiveOsiMenus = flow([
      Object.entries,
      (arr) => arr.filter(([, menu]: [string, any]) => menu.active),
      (arr) => arr?.map(([, menu]: [string, any]) => menu)
    ]);

    return getAdaptedActiveOsiMenus(this.osiMenus);
  }

  get isWizardFinished() {
    return this.osiInfo?.wizardStep === 'finish';
  }

  get isFreeMode() {
    return this.osiInfo?.registrationType === 'FREE';
  }

  hydrateOsi = async (): Promise<void> => {
    const { location } = window;
    const isOsi = location.pathname.includes('/osi');

    if (!this.osiId || !isOsi) return;

    await this.selectOsi(this.osiId, false);
  };

  selectOsi = async (osiId: number, withRedirect = true): Promise<void> => {
    try {
      if (this.osiId) {
        this.exitOsi();
      }
      this.osiId = osiId;

      // OSI-816: дом может стать недоступен (скрыт у председателя → 403, удалён → 404),
      // а его osiId остаётся в localStorage. Раньше ошибка здесь давала только тост, osiId не
      // сбрасывался, и после перезагрузки /osi/* висел вечным LoadingScreen.
      // Сбрасываем выбор ТОЛЬКО при ошибке загрузки самого дома (getOsiInfoById): сбой
      // docs/menus — побочный, из дома из-за него не выкидываем (поведение как раньше).
      // 401 сюда не попадает: интерцептор его не пробрасывает (sessionExpired сам уводит на вход).
      try {
        await this.loadOsiInfo();
      } catch (e) {
        this.handleOsiUnavailable();
        return;
      }

      await this.loadOsiDocs();

      if (!this.osiInfo) throw new Error('osiInfo not found');

      if (this.osiInfo?.wizardStep !== 'finish') {
        if (withRedirect) {
          this.history.goToWizard();
          return;
        }
      }

      await this.loadOsiMenus();

      if (withRedirect) {
        this.history.goToOsiRoot();
      }
    } catch (e: any) {
      if (e instanceof Error) {
        notifications.error(e?.message ?? e);
        logger.error(e);
        return;
      }
      logger.error(e);
      notifications.error(e);
    }
  };

  refreshOsi = async (): Promise<void> => {
    await this.loadOsiInfo();
    await this.loadOsiMenus();
  };

  exitOsi = (): void => {
    this.osiId = initialValues.osiId;
    this.osiInfo = initialValues.osiInfo;
    this.osiMenus = initialValues.osiMenus;
    this.isLoading = initialValues.isLoading;
  };

  /**
   * OSI-816: выбранный дом недоступен — сбрасываем выбор и уводим в /cabinet.
   * exitOsi() обнуляет osiId, persist сразу запишет null в localStorage → после перезагрузки
   * hydrateOsi() уже не будет повторять запрос.
   * Цикла нет: /cabinet никуда не редиректит, а OsiSelectedGuard при пустом osiId сам ведёт
   * на /cabinet (тот же адрес). `replace` — чтобы «Назад» не вернул на /osi/* недоступного дома.
   * Если мы уже на /cabinet (клик по карточке) — никуда не переходим, достаточно тоста.
   */
  private handleOsiUnavailable = (): void => {
    this.exitOsi();
    notifications.error('common:osiUnavailable');

    if (!window.location.pathname.startsWith(PATH_CABINET.root)) {
      this.history.navigateTo(PATH_CABINET.root, { replace: true });
    }
  };

  // Чистим persist сессии: сбрасываем поля к initial (exitOsi + остальные), затем удаляем
  // localStorage-ключ (персистится osiId). Иначе выбранный ОСИ протекает между входами.
  clearSession = async (): Promise<void> => {
    this.exitOsi();
    this.osiDocs = initialValues.osiDocs;
    this.isLockedMenus = initialValues.isLockedMenus;
    await clearPersistedStore(this);
  };

  loadOsiInfo = async (): Promise<void> => {
    try {
      if (!this.osiId) return;

      this.isLoading = true;
      this.osiInfo = await getOsiInfoById(this.osiId);
    } catch (e) {
      logger.error(e);
      throw e;
    } finally {
      this.isLoading = false;
    }
  };

  loadOsiDocs = async (): Promise<void> => {
    try {
      if (!this.osiId) return;

      this.isLoading = true;
      this.osiDocs = await getOsiDocsById(this.osiId);
    } catch (e) {
      logger.error(e);
      throw e;
    } finally {
      this.isLoading = false;
    }
  };

  loadOsiMenus = async (): Promise<void> => {
    try {
      if (!this.osiId) return;
      // TODO: use shared acts api this

      const notSignedActs = (await api.OsiNotSignedActs(this.osiId)) as unknown as any[];

      /** Если у ОСИ есть не подписанные акты - блокируем разделы меню, кроме Актов
       * *Для админа условие не действует*
       * */
      if (notSignedActs?.length > 0) {
        if (!this.rolesModule.isHasAdminRole) {
          this.activatePartialMenus();
          this.isLockedMenus = true;
          return;
        }
      }

      this.activateAllMenus();
      this.isLockedMenus = false;

      this.isLoading = true;
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  activatePartialMenus = () => {
    this.osiMenus.acts.active = true;
  };

  activateAllMenus = () => {
    this.osiMenus.info.active = true;
    // this.osiMenus.services.active = true;
    this.osiMenus.abonents.active = true;
    this.osiMenus.accurals.active = true;
    this.osiMenus.osv.active = true;
    this.osiMenus.payments.active = true;
    this.osiMenus.acts.active = true;
    this.osiMenus.reports.active = true;
    this.osiMenus.debts.active = true;
    this.osiMenus.invoices.active = true;
  };
}
